import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ChevronDown, Github, KeyRound, Lock, ShieldCheck, Twitter, Youtube } from "lucide-react";
import Logo from "@/components/Logo";
import { FOOTER, FOOTER_BOTTOM, GAMES_STRIP, TRUST_BADGES } from "@/content/site";
import { openCookieSettings } from "./CookieBanner";
import { API } from "@/lib/auth";

const BADGE_ICONS = { Lock, KeyRound, ShieldCheck, Activity };

// L'indicateur interroge réellement l'API : il ne ment jamais sur l'état du service.
export function useServiceHealth() {
  const [state, setState] = useState("checking");
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    fetch(`${API}/health`, { signal: controller.signal })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(d => { if (!cancelled) setState(d.status === "ok" ? "ok" : "degraded"); })
      .catch(() => { if (!cancelled) setState("down"); })
      .finally(() => clearTimeout(timer));
    return () => { cancelled = true; controller.abort(); clearTimeout(timer); };
  }, []);
  return state;
}

const HEALTH_LABELS = { checking: "Vérification…", ok: "Tous les services opérationnels", degraded: "Service dégradé", down: "Service indisponible" };

function Column({ col, openKey, setOpenKey }) {
  const isOpen = openKey === col.title;
  return (
    <div className={`footer-col${isOpen ? " is-open" : ""}`}>
      {/* Titre cliquable en mobile (accordéon), simple en-tête au-delà. */}
      <button
        type="button" className="footer-col-head"
        aria-expanded={isOpen} data-testid={`footer-col-${col.title.toLowerCase()}`}
        onClick={() => setOpenKey(isOpen ? null : col.title)}
      >
        <h4>{col.title}</h4><ChevronDown size={14} aria-hidden="true" />
      </button>
      <div className="footer-col-links">
        {col.links.map(l => (
          <Link key={l.to + l.label} to={l.to}>
            {l.label}{l.badge && <span className="footer-badge">{l.badge}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function SiteFooter() {
  const health = useServiceHealth();
  const [openKey, setOpenKey] = useState(null);
  const dot = health === "ok" ? "" : health === "checking" ? " is-wait" : " is-down";

  return (
    <footer className="site-footer">
      <div className="footer-games">
        <span className="footer-games-label">Jeux couverts</span>
        <div className="pill-row">
          {GAMES_STRIP.map(g => <Link key={g.slug} to={`/games/${g.slug}`} className="pill">{g.label}</Link>)}
        </div>
      </div>

      <div className="footer-cols">
        <div className="footer-brand">
          <Logo />
          <p>Le coaching esport, séance par séance. Un peu mieux, chaque jour.</p>
          <div className="footer-social">
            <a href="https://x.com/performcustom" aria-label="X" target="_blank" rel="noreferrer noopener"><Twitter size={14} /></a>
            <a href="https://github.com/performcustom" aria-label="GitHub" target="_blank" rel="noreferrer noopener"><Github size={14} /></a>
            <a href="https://youtube.com/@performcustom" aria-label="YouTube" target="_blank" rel="noreferrer noopener"><Youtube size={14} /></a>
          </div>
        </div>
        {FOOTER.map(col => <Column key={col.title} col={col} openKey={openKey} setOpenKey={setOpenKey} />)}
      </div>

      {/* Mesures réellement en place. Aucune certification n'est revendiquée. */}
      <div className="footer-trust">
        {TRUST_BADGES.map(b => {
          const Icon = BADGE_ICONS[b.icon];
          return <Link key={b.label} to={b.to} className="trust-badge">{Icon && <Icon size={13} />}{b.label}</Link>;
        })}
        <Link to="/trust-center" className="trust-badge is-link">Voir le Trust Center</Link>
      </div>

      <div className="footer-bottom">
        <span>Perform © {new Date().getFullYear()} · Fait en France.</span>
        <nav className="footer-bottom-links" aria-label="Liens légaux">
          {FOOTER_BOTTOM.map(l => <Link key={l.to} to={l.to}>{l.label}</Link>)}
          <button type="button" onClick={openCookieSettings} data-testid="footer-cookie-settings">Préférences cookies</button>
        </nav>
        <Link to="/statut" className="footer-status" data-testid="footer-status">
          <i className={dot} aria-hidden="true" />{HEALTH_LABELS[health]}
        </Link>
      </div>
    </footer>
  );
}
